import { useMemo, useState } from 'react'
import { CLink } from './CLink'
import { CustomerButton } from './CustomerButton'
import { isEditor } from '../utils/misc'
import { useBackground } from '../utils/hooks'
import { useScroll } from '../utils/hooks'
import { ResponsiveImage } from './ResponsiveImage'
import { Icon } from './Icon'
import { LucideIconName } from '@bm-js/atlascloud-shared'
import { NavPage, NavProps } from '../utils/types'

export const Nav3 = <PAGE extends NavPage>({
  dark,
  logoWide,
  navTree,
  navBackground,
  contactInfo,
  setActivePageFromNav,
  LinkComponent,
  scrollElement,
}: NavProps<PAGE>) => {
  const { isScrolled } = useScroll(scrollElement)
  const [mobileMenuExpanded, setMobileMenuExpanded] = useState(false)

  const groupedPages = useMemo(() => {
    const lastPage = navTree[navTree.length - 1]
    const otherPages = navTree.slice(0, -1)
    otherPages.splice(0, 1)
    return {
      lastPage,
      otherPages,
    }
  }, [navTree])

  const positionClass = useMemo(() => {
    if (isEditor()) return 'absolute'
    return 'fixed'
  }, [])

  const breakpointClasses = useMemo(() => {
    let desktopNav = 'md:py-6 md:px-20'
    let desktopNavLinks = 'md:flex'
    let hamburger = 'md:hidden'
    let mobileNav = 'md:hidden'

    if (isEditor()) {
      desktopNav = '@2xl/top:py-6 @2xl/top:px-20'
      desktopNavLinks = '@2xl/top:flex'
      hamburger = '@2xl/top:hidden'
      mobileNav = '@2xl/top:hidden'
    }

    return {
      desktopNav,
      desktopNavLinks,
      hamburger,
      mobileNav,
    }
  }, [])

  const navBgBackground = useBackground(navBackground)

  const displayAsDark = useMemo(() => dark || isScrolled, [dark, isScrolled])

  const colorClasses = useMemo(() => {
    let text = 'text-neutral-100'
    let image = ''
    if (displayAsDark) {
      text = 'text-neutral-800'
      image = 'invert'
    }

    return {
      text,
      image,
    }
  }, [displayAsDark])

  const transitionClasses = 'transition-all duration-300'

  return (
    <nav>
      <div
        className={`${positionClass} ${
          breakpointClasses.desktopNav
        } top-0 left-0 right-0 z-30 flex w-full items-center justify-between p-6 ${transitionClasses} ${
          isScrolled ? navBgBackground + ' shadow' : 'bg-transparent'
        }`}
      >
        <CLink
          href='/'
          editorClick={() => setActivePageFromNav?.(navTree[0])}
          LinkComponent={LinkComponent}
        >
          {!!logoWide && (
            <ResponsiveImage
              className={`w-20 ${colorClasses.image} ${transitionClasses}`}
              file={logoWide}
            />
          )}
        </CLink>
        <div
          className={`${breakpointClasses.desktopNavLinks} hidden items-center gap-8`}
        >
          {groupedPages.otherPages.map((page) => (
            <CLink
              key={page.uuid}
              href={`${page.slug}`}
              className={`${colorClasses.text} ${transitionClasses} font-bold`}
              editorClick={() => setActivePageFromNav?.(page)}
              LinkComponent={LinkComponent}
            >
              {page.viewName}
            </CLink>
          ))}
        </div>
        <CLink
          href={`/${groupedPages.lastPage.slug}`}
          className={`hidden clean ${breakpointClasses.desktopNavLinks}`}
          editorClick={() => setActivePageFromNav?.(groupedPages.lastPage)}
          LinkComponent={LinkComponent}
        >
          <CustomerButton
            btnType='outline'
            className={`font-bold ${transitionClasses}`}
            btnColor={displayAsDark ? 'dark' : 'light'}
          >
            {groupedPages.lastPage.viewName}
          </CustomerButton>
        </CLink>
        <button
          className={`${breakpointClasses.hamburger} ${transitionClasses} ${colorClasses.text} block`}
          onClick={() => setMobileMenuExpanded(true)}
        >
          <Icon name={LucideIconName.Menu} />
        </button>
      </div>
      <button
        className={`${positionClass} ${
          mobileMenuExpanded
            ? 'pointer-events-auto opacity-100'
            : 'pointer-events-none opacity-0'
        } inset-0 z-40 w-full bg-black/50 transition-opacity`}
        onClick={() => setMobileMenuExpanded(false)}
      />
      <div
        className={`${positionClass} ${
          mobileMenuExpanded ? '' : ' translate-y-full'
        } ${
          breakpointClasses.mobileNav
        } bottom-0 left-0 right-0 z-50 block w-full rounded-t-[32px] ${navBgBackground} px-8 pb-24 text-black transition-transform duration-500`}
      >
        <div className='flex justify-end py-6'>
          <button
            onClick={() => setMobileMenuExpanded(false)}
            className='text-neutral-800'
          >
            <Icon name={LucideIconName.X} />
          </button>
        </div>
        <div className='flex flex-col justify-start gap-6'>
          {[...groupedPages.otherPages, groupedPages.lastPage].map((page) => (
            <CLink
              key={page.uuid}
              href={`/${page.slug}`}
              className='text-2xl font-medium text-neutral-800'
              editorClick={() => setActivePageFromNav?.(page)}
              onClick={() => setMobileMenuExpanded(false)}
              LinkComponent={LinkComponent}
            >
              {page.viewName}
            </CLink>
          ))}
        </div>
        <CLink
          href={`mailto:${contactInfo.email}`}
          LinkComponent={LinkComponent}
        >
          <CustomerButton
            btnType='outline'
            className='mt-16 text-sm font-bold'
            btnColor='dark'
          >
            {contactInfo.email}
          </CustomerButton>
        </CLink>
      </div>
    </nav>
  )
}
